import React, { useState } from "react";
import { Modal } from "@shared/controls/Modal";
import PropertyVideoPlayer from "./PropertyVideoPlayer";

interface PropertyImageGalleryProps {
  coverImage?: string;
  otherImages?: string[];
  videoUrl?: string;
  title?: string;
  slug?: string;
}

const PropertyImageGallery: React.FC<PropertyImageGalleryProps> = ({
  coverImage,
  otherImages,
  videoUrl,
  title,
  slug,
}) => {
  // Dummy image placeholder
  const dummyImage =
    "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300' viewBox='0 0 400 300'%3E%3Crect fill='%23f0f0f0' width='400' height='300'/%3E%3Ctext fill='%23999' font-family='Arial' font-size='18' x='50%25' y='50%25' text-anchor='middle' dy='.3em'%3ENo Image Available%3C/text%3E%3C/svg%3E";

  const images = [coverImage, ...(otherImages || [])].filter(
    Boolean,
  ) as string[];

  const [showAllImagesModal, setShowAllImagesModal] = useState(false);
  const [zoomedImageIndex, setZoomedImageIndex] = useState<number | null>(null);

  const galleryImages = images.slice(0, 5);
  const hasMoreImages = (otherImages?.length || 0) > 4;

  const mainDisplayImage = videoUrl || coverImage || dummyImage;

  const handleZoomImage = (index: number) => {
    setZoomedImageIndex(index);
  };

  const handleCloseZoom = () => {
    setZoomedImageIndex(null);
  };

  const handleNextImage = () => {
    setZoomedImageIndex((prev) =>
      prev === null ? 0 : (prev + 1) % images.length,
    );
  };

  const handlePrevImage = () => {
    setZoomedImageIndex((prev) =>
      prev === null ? 0 : (prev - 1 + images.length) % images.length,
    );
  };

  return (
    <div className="mb-4">
      <div className="row g-3">
        <div className="col-md-6">
          <div className="position-relative h-100">
            {videoUrl ? (
              <PropertyVideoPlayer url={videoUrl} title={title} />
            ) : (
              <img
                src={mainDisplayImage}
                alt={slug}
                title={title}
                className="w-100 h-100 rounded-4"
                style={{ objectFit: "cover", minHeight: "350px" }}
              />
            )}
          </div>
        </div>

        <div className="col-md-6">
          <div className="row g-3 h-100">
            {Array.from({ length: 4 }, (_, i) => {
              const img = galleryImages[i + 1];
              return (
                <div className="col-6" key={i}>
                  <div className="h-100" style={{ minHeight: "200px" }}>
                    <img
                      src={img || dummyImage}
                      alt={slug}
                      title={title}
                      className="w-100 h-100 rounded-4"
                      style={{ objectFit: "cover" }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {hasMoreImages && (
        <div className="border mt-3 w-max rounded-3">
          <button
            className="btn text-black fs-14 font-onest text-decoration-none"
            onClick={() => setShowAllImagesModal(true)}
            style={{ color: "#47BBA9" }}
          >
            View All Photos
          </button>
        </div>
      )}

      <Modal
        show={showAllImagesModal}
        onClose={() => setShowAllImagesModal(false)}
        size="lg"
      >
        <h5 className="mb-3">All Photos</h5>
        <div
          className="row g-3"
          style={{ maxHeight: "70vh", overflowY: "auto" }}
        >
          {images.map((img, index) => (
            <div className="col-md-4 col-6" key={index}>
              <img
                src={img}
                alt={`${title} ${index + 1}`}
                className="w-100 rounded-4"
                style={{ objectFit: "cover", cursor: "pointer" }}
                onClick={() => handleZoomImage(index)}
              />
            </div>
          ))}
        </div>
      </Modal>

      {zoomedImageIndex !== null && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ backgroundColor: "rgba(0, 0, 0, 0.95)", zIndex: 9999 }}
          onClick={handleCloseZoom}
        >
          <button
            className="position-absolute top-0 end-0 m-3 btn btn-light rounded-circle"
            style={{ width: "40px", height: "40px" }}
            onClick={handleCloseZoom}
          >
            <i className="fa fa-times"></i>
          </button>

          <button
            className="position-absolute top-50 start-0 translate-middle-y m-3 btn btn-light rounded-circle"
            style={{ width: "40px", height: "40px" }}
            onClick={(e) => {
              e.stopPropagation();
              handlePrevImage();
            }}
          >
            <i className="fa fa-chevron-left"></i>
          </button>

          <img
            src={images[zoomedImageIndex]}
            alt={`${title} ${zoomedImageIndex + 1}`}
            className="max-w-100 max-h-100"
            style={{
              objectFit: "contain",
              maxHeight: "90vh",
              maxWidth: "90vw",
            }}
            onClick={(e) => e.stopPropagation()}
          />

          <button
            className="position-absolute top-50 end-0 translate-middle-y m-3 btn btn-light rounded-circle"
            style={{ width: "40px", height: "40px" }}
            onClick={(e) => {
              e.stopPropagation();
              handleNextImage();
            }}
          >
            <i className="fa fa-chevron-right"></i>
          </button>

          <div className="position-absolute bottom-0 start-50 translate-middle-x mb-3 text-white">
            {zoomedImageIndex + 1} / {images.length}
          </div>
        </div>
      )}
    </div>
  );
};

export default PropertyImageGallery;
